import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import journeySteps from "../data/journey";

// Optimised step images (1400px wide JPEGs, ~150-450KB each)
const stepArt = [
  "/journey/books-coffee.jpg",
  "/journey/festive-stories.jpg",
  "/journey/coffee-love.jpg",
  "/journey/the-proposal.jpg",
  "/journey/luxury-packaging.jpg",
  "/journey/velvet-boxes.jpg",
  "/journey/baby-jungle.jpg",
];

const AUTOPLAY_MS = 5000;

function circleClass(isActive, isDone) {
  if (isActive) {
    return "bg-[#8B4513] text-[#FDFBF7] shadow-[0_14px_30px_-12px_rgba(139,69,19,0.65)] scale-105";
  }
  if (isDone) {
    return "bg-[#FDFBF7] text-[#8B4513] shadow-[0_8px_20px_-12px_rgba(42,29,21,0.35)]";
  }
  return "bg-[#FDFBF7] text-[#2A1D15] shadow-[0_8px_20px_-12px_rgba(42,29,21,0.25)] group-hover:text-[#8B4513]";
}

function labelClass(isActive, isDone) {
  if (isActive) return "text-[#8B4513]";
  if (isDone) return "text-[#8B4513]/60";
  return "text-[#5C4334]/65 group-hover:text-[#8B4513]/85";
}

function useSectionInView(ref) {
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold: 0.2 }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return inView;
}

function useAutoplay(active, setActive, inView) {
  useEffect(() => {
    if (!inView) return;
    const t = setTimeout(() => {
      setActive((i) => (i + 1) % journeySteps.length);
    }, AUTOPLAY_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, inView]);
}

function ProgressRing() {
  return (
    <svg
      aria-hidden
      className="absolute -inset-[5px] w-[calc(100%+10px)] h-[calc(100%+10px)] -rotate-90"
      viewBox="0 0 100 100"
    >
      <motion.circle
        cx="50"
        cy="50"
        r="47"
        fill="none"
        stroke="#8B4513"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeDasharray="295.3"
        initial={{ strokeDashoffset: 295.3 }}
        animate={{ strokeDashoffset: 0 }}
        transition={{ duration: AUTOPLAY_MS / 1000, ease: "linear" }}
      />
    </svg>
  );
}

function StepConnector({ filled, animateFill }) {
  return (
    <div className="relative flex-1 h-px bg-[#8B4513]/20 mt-[22px] md:mt-[30px] mx-1 md:mx-2 overflow-hidden">
      {filled && (
        <motion.span
          initial={{ width: animateFill ? "0%" : "100%" }}
          animate={{ width: "100%" }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="absolute inset-y-0 left-0 bg-[#8B4513]/60"
        />
      )}
    </div>
  );
}

function StepButton({ step, isActive, isDone, onSelect }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      data-testid={`journey-step-${step.number}`}
      aria-label={`Go to step ${step.number} ${step.title}`}
      className="group flex flex-col items-center shrink-0 focus:outline-none"
      style={{ width: "min(13%, 120px)" }}
    >
      <span
        className={`relative flex items-center justify-center w-11 h-11 md:w-[60px] md:h-[60px] rounded-full font-serif-display text-base md:text-xl transition-all duration-500 ${circleClass(
          isActive,
          isDone
        )}`}
      >
        {step.number}
        {isActive && <ProgressRing key={`ring-${step.number}`} />}
      </span>
      <span
        className={`hidden md:block mt-4 w-[130px] text-[10px] uppercase tracking-[0.13em] font-medium text-center leading-relaxed transition-colors ${labelClass(
          isActive,
          isDone
        )}`}
      >
        {step.title}
      </span>
    </button>
  );
}

function Stepper({ active, onSelect }) {
  return (
    <div className="flex items-start" data-testid="journey-stepper">
      {journeySteps.map((s, idx) => (
        <React.Fragment key={s.number}>
          {idx > 0 && (
            <StepConnector filled={idx <= active} animateFill={idx === active} />
          )}
          <StepButton
            step={s}
            isActive={idx === active}
            isDone={idx < active}
            onSelect={() => onSelect(idx)}
          />
        </React.Fragment>
      ))}
    </div>
  );
}

function StepContent({ step }) {
  return (
    <motion.div
      key={`txt-${step.number}`}
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className="order-2 lg:order-1"
    >
      <p
        aria-hidden
        className="font-serif-display leading-none text-[#2A1D15]/[0.12] select-none"
        style={{ fontSize: "clamp(5rem, 9vw, 8rem)" }}
      >
        {step.number}
      </p>
      <h3 className="mt-2 font-serif-display text-4xl md:text-5xl leading-[1.02] tracking-tight text-[#2A1D15]">
        {step.title}
      </h3>
      <p className="mt-5 text-base md:text-lg text-[#5C4334] font-light leading-[1.85] max-w-xl">
        {step.description}
      </p>
    </motion.div>
  );
}

function StepSlides({ active }) {
  return (
    <div className="order-1 lg:order-2 relative aspect-[4/3] overflow-hidden rounded-t-[28px] shadow-[0_40px_90px_-32px_rgba(42,29,21,0.5)]">
      {journeySteps.map((step, idx) => {
        const isActive = idx === active;
        return (
          <motion.div
            key={step.number}
            initial={false}
            animate={{
              opacity: isActive ? 1 : 0,
              scale: isActive ? 1.05 : 1.01,
              zIndex: isActive ? 2 : 0,
            }}
            transition={{
              opacity: { duration: 0.9, ease: [0.22, 1, 0.36, 1] },
              scale: { duration: AUTOPLAY_MS / 1000 + 0.5, ease: "easeOut" },
            }}
            className="absolute inset-0"
          >
            <img
              src={stepArt[idx]}
              alt={step.title}
              loading="eager"
              decoding="async"
              className="absolute inset-0 w-full h-full object-cover"
            />
          </motion.div>
        );
      })}
    </div>
  );
}

function JourneyHeader() {
  return (
    <div className="text-center max-w-3xl mx-auto mb-14 md:mb-16">
      <p
        className="overline text-[#8B4513] mb-6 inline-flex items-center gap-3"
        data-testid="journey-overline"
      >
        <span className="w-8 h-px bg-[#8B4513]/60" />
        What we do · The Journey
        <span className="w-8 h-px bg-[#8B4513]/60" />
      </p>
      <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-[#2A1D15]">
        The journey <span className="italic text-[#8B4513]">of a gift.</span>
      </h2>
      <p className="mt-6 text-base md:text-lg text-[#5C4334] font-light leading-[1.85] max-w-xl mx-auto">
        "We believe it's the little things that matter." Follow what happens
        from the moment you reach out — tap each step to see how an idea
        becomes a memory.
      </p>
    </div>
  );
}

export default function Journey() {
  const [active, setActive] = useState(0);
  const sectionRef = useRef(null);
  const inView = useSectionInView(sectionRef);
  useAutoplay(active, setActive, inView);

  const goTo = (idx) => {
    setActive(((idx % journeySteps.length) + journeySteps.length) % journeySteps.length);
  };

  return (
    <section
      id="journey"
      data-testid="journey-section"
      ref={sectionRef}
      className="relative py-24 md:py-32 bg-[#ebe0cd] overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute -top-40 right-0 w-[420px] h-[420px] rounded-full bg-[#d19c72]/15 blur-3xl pointer-events-none"
      />

      <div className="max-w-[1340px] mx-auto px-6 md:px-10 lg:px-16 xl:px-24">
        <JourneyHeader />

        <div className="max-w-[1140px] mx-auto">
          <Stepper active={active} onSelect={goTo} />

          <div className="mt-12 md:mt-16 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <StepContent step={journeySteps[active]} />
            <StepSlides active={active} />
          </div>
        </div>
      </div>
    </section>
  );
}
